import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { AppThunk } from "@store/index";
import {
  AggregatedPackingPropsResponse,
  CargoReleaseResponse,
  ContainerWarehousingCreate,
  CreateCargoRelease,
  CreateDepositorChangeForm,
  CreatePickingServiceForm,
  DepositorChangeResponse,
  FCRItemsProps,
  PackingProps,
  PackingPropsResponse,
  PickingServiceResponse,
  ShiftingCreate,
  ShiftingDetailsResponse,
  UnloadingBulk,
  UnloadingContainer,
  UnloadingItem,
  UnloadingItemResponse,
  UnloadingNotes,
  WarehousingResponse,
} from "./types";
import {
  cancelShiftingApi,
  getAggregatedPackingShortApi,
  getBulkUnloadingApi,
  getDepositaryApi,
  getFcrApi,
  getFcrPackingListApi,
  getPackingListByShiftingApi,
  getPackingShortApi,
  getPickingOrdersApi,
  getShiftingApi,
  getShiftingDeliveryReadinessApi,
  getUnloadingApi,
  getWarehousingApi,
  removeDepositaryApi,
  removeFcrApi,
  removePickingOrdersApi,
  removeShiftingApi,
  updateBulkUnloadingApi,
  updateContainerUnloadingApi,
  updateDepositaryApi,
  updateFcrApi,
  updateFcrPackingListApi,
  updatePickingOrdersApi,
  updateShiftingApi,
  updateUnloadingNotesApi,
  updateWarehousingApi,
} from "./api";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type UnloadingState = {
  bulk: ViewState<UnloadingItem[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  unloading: ViewState<UnloadingItem[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  packing: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
    meta?: { items: { id: number; id_number: string }[] };
  };
  packingByShifting: Record<
    number,
    ViewState<PackingProps[] | null> & {
      total: number;
      page: number;
      size: number;
    }
  >;
  release: ViewState<CargoReleaseResponse[] | null>;
  released: ViewState<FCRItemsProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  warehousing: ViewState<WarehousingResponse | null>;
  shifting: ViewState<ShiftingDetailsResponse[] | null>;
  depositary: ViewState<DepositorChangeResponse[] | null>;
  pickingOrders: ViewState<PickingServiceResponse[] | null>;
  notes: ViewState<UnloadingNotes | null>;
  aggregated: ViewState<AggregatedPackingPropsResponse | null>;
  shiftingReadiness: {
    ready?: boolean;
    missed_columns?: string[];
    empty?: boolean;
    loading: boolean;
    error: string | null;
  };
};

export type UnloadingKey =
  | "packing"
  | "unloading"
  | "warehousing"
  | "shifting"
  | "depositary"
  | "pickingOrders"
  | "release"
  | "released"
  | "bulk"
  | "notes"
  | "aggregated";

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: UnloadingState = {
  unloading: {
    ...getInitialState<UnloadingItem[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  packing: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  packingByShifting: {},
  warehousing: getInitialState<WarehousingResponse | null>(null),
  shifting: getInitialState<ShiftingDetailsResponse[] | null>(null),
  depositary: getInitialState<DepositorChangeResponse[] | null>(null),
  pickingOrders: getInitialState<PickingServiceResponse[] | null>(null),
  release: getInitialState<CargoReleaseResponse[] | null>(null),
  released: {
    ...getInitialState<FCRItemsProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  bulk: {
    ...getInitialState<UnloadingItem[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  notes: getInitialState<UnloadingNotes | null>(null),
  aggregated: getInitialState<AggregatedPackingPropsResponse | null>(null),
  shiftingReadiness: {
    ready: undefined,
    empty: undefined,
    missed_columns: undefined,
    loading: false,
    error: null,
  },
};

const unloadingSlice = createSlice({
  name: "unloading",
  initialState,
  reducers: {
    getStart(state: UnloadingState, action: PayloadAction<UnloadingKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getSuccess(
      state: UnloadingState,
      action: PayloadAction<{
        key: UnloadingKey;
        items: any;
        total?: number;
        page?: number;
        size?: number;
      }>,
    ) {
      const { key, items, page, total, size } = action.payload;

      const slice = state[key];

      slice.items = items;
      slice.loading = false;
      slice.error = null;

      if ("total" in slice && typeof total === "number") {
        slice.total = total;
      }

      if ("page" in slice && typeof page === "number") {
        slice.page = page;
      }

      if ("size" in slice && typeof size === "number") {
        slice.size = size;
      }
    },

    getUnloadingSuccess(
      state: UnloadingState,
      action: PayloadAction<UnloadingItemResponse>,
    ) {
      const { items, total, page, size } = action.payload;
      state.unloading.items = items;
      state.unloading.total = total;
      state.unloading.page = page;
      state.unloading.size = size;
      state.unloading.loading = false;
    },
    createSuccess(
      state: UnloadingState,
      action: PayloadAction<{ key: UnloadingKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    setBulkPackingList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: UnloadingBulk }>,
    ) {
      const { data } = action.payload;
      if (!state.unloading.items) return;

      const idx = state.unloading.items.findIndex(
        (item) => item.bl_id === data.bl_id,
      );
      if (idx !== -1) {
        state.unloading.items[idx].unloading = data;
      }
    },
    setContainerPackingList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: UnloadingContainer }>,
    ) {
      const { data } = action.payload;
      if (!state.unloading.items) return;

      const idx = state.unloading.items.findIndex(
        (item) => item.id === data.bl_container_id,
      );
      if (idx !== -1) {
        state.unloading.items[idx].unloading = data;
      }
    },
    setShiftingList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: ShiftingDetailsResponse }>,
    ) {
      const { data } = action.payload;
      if (!state.shifting.items) return;

      const idx = state.shifting.items.findIndex((item) => item.id === data.id);
      if (idx !== -1) {
        state.shifting.items[idx] = data;
      } else {
        state.shifting.items.push(data);
      }
    },
    setShiftingRemove(state: UnloadingState, action: PayloadAction<number>) {
      const id = action.payload;
      if (!state.shifting.items) return;

      state.shifting.items = state.shifting.items.filter(
        (item) => item.id !== id,
      );
    },
    setShiftingCancel(
      state: UnloadingState,
      action: PayloadAction<ShiftingDetailsResponse>,
    ) {
      const data = action.payload;
      if (!state.shifting.items) return;
      const idx = state.shifting.items.findIndex((item) => item.id === data.id);
      if (idx !== -1) {
        state.shifting.items[idx] = data;
      }
    },
    setDepositaryList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: DepositorChangeResponse }>,
    ) {
      const { data } = action.payload;
      if (!state.depositary.items) return;

      const idx = state.depositary.items.findIndex(
        (item) => item.id === data.id,
      );
      if (idx !== -1) {
        state.depositary.items[idx] = data;
      } else {
        state.depositary.items.push(data);
      }
    },
    setDepositaryRemove(state: UnloadingState, action: PayloadAction<number>) {
      const id = action.payload;
      if (!state.depositary.items) return;

      state.depositary.items = state.depositary.items.filter(
        (item) => item.id !== id,
      );
    },
    setPickingOrdersList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: PickingServiceResponse }>,
    ) {
      const { data } = action.payload;
      if (!state.pickingOrders.items) return;

      const idx = state.pickingOrders.items.findIndex(
        (item) => item.id === data.id,
      );
      if (idx !== -1) {
        state.pickingOrders.items[idx] = data;
      } else {
        state.pickingOrders.items.push(data);
      }
    },
    setPickingOrdersRemove(
      state: UnloadingState,
      action: PayloadAction<number>,
    ) {
      const id = action.payload;
      if (!state.pickingOrders.items) return;

      state.pickingOrders.items = state.pickingOrders.items.filter(
        (item) => item.id !== id,
      );
    },
    setFcrReleased(
      state: UnloadingState,
      action: PayloadAction<{
        id: number;
        data: {
          packing_list_ids: number[];
          release_date: string;
        };
      }>,
    ) {
      const { data } = action.payload;
      state.released.items?.forEach((item) => {
        if (data.packing_list_ids.includes(item.packing_list.id)) {
          item.release_date = data.release_date;
        }
      });
    },
    setFcrList(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: CargoReleaseResponse }>,
    ) {
      const { data } = action.payload;
      if (!state.release.items) return;

      const idx = state.release.items.findIndex((item) => item.id === data.id);
      if (idx !== -1) {
        state.release.items[idx] = data;
      } else {
        state.release.items.push(data);
      }
    },
    setFcrRemove(state: UnloadingState, action: PayloadAction<number>) {
      const id = action.payload;
      if (!state.release.items) return;

      state.release.items = state.release.items.filter(
        (item) => item.id !== id,
      );
    },
    getPackingSuccess(
      state: UnloadingState,
      action: PayloadAction<PackingPropsResponse>,
    ) {
      const { items, total, page, size, meta } = action.payload;
      state.packing.items = items;
      state.packing.total = total;
      state.packing.page = page;
      state.packing.size = size;
      state.packing.loading = false;
      state.packing.meta = meta;
    },
    getPackingByShiftingSuccess(
      state: UnloadingState,
      action: PayloadAction<{ id: number; data: PackingPropsResponse }>,
    ) {
      const { id, data } = action.payload;
      const { items, total, page, size } = data;

      state.packingByShifting[id] = {
        items,
        total,
        page,
        size,
        loading: false,
        success: null,
        error: null,
      };
      state.packing.loading = false;
      state.packing.error = null;
    },
    cleanPackingByShifting(
      state: UnloadingState,
      action: PayloadAction<number>,
    ) {
      const id = action.payload;
      delete state.packingByShifting[id];
    },
    getPackingByShiftingStart(
      state: UnloadingState,
      action: PayloadAction<number>,
    ) {
      const id = action.payload;
      if (!state.packingByShifting[id]) {
        state.packingByShifting[id] = {
          items: null,
          total: 0,
          page: 0,
          size: 0,
          loading: false,
          success: null,
          error: null,
        };
      }
      state.packingByShifting[id].loading = true;
      state.packingByShifting[id].error = null;
    },
    getPackingByShiftingFailure(
      state: UnloadingState,
      action: PayloadAction<{ id: number; error: string }>,
    ) {
      const { id, error } = action.payload;
      if (state.packingByShifting[id]) {
        state.packingByShifting[id].loading = false;
        state.packingByShifting[id].error = error;
      }
    },
    getFailure(
      state: UnloadingState,
      action: PayloadAction<{ key: UnloadingKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state[key].loading = false;
      state[key].error = error;
    },
    cleanData(state: UnloadingState, action: PayloadAction<UnloadingKey>) {
      state[action.payload].items = null;
    },
    hideWarning(state: UnloadingState, action: PayloadAction<UnloadingKey>) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
    },
    setShiftingReadiness(
      state: UnloadingState,
      action: PayloadAction<{
        ready?: boolean;
        missed_columns?: string[];
        empty?: boolean;
      }>,
    ) {
      state.shiftingReadiness.ready = action.payload.ready;
      state.shiftingReadiness.missed_columns = action.payload.missed_columns;
      state.shiftingReadiness.empty = action.payload.empty;
      state.shiftingReadiness.loading = false;
      state.shiftingReadiness.error = null;
    },
    setShiftingReadinessLoading(state: UnloadingState) {
      state.shiftingReadiness.loading = true;
      state.shiftingReadiness.error = null;
    },
    cleanShiftingReadiness(state: UnloadingState) {
      state.shiftingReadiness.ready = undefined;
      state.shiftingReadiness.missed_columns = undefined;
      state.shiftingReadiness.empty = undefined;
      state.shiftingReadiness.loading = false;
      state.shiftingReadiness.error = null;
    },
  },
});

export const {
  getStart,
  cleanData,
  setFcrList,
  getSuccess,
  getFailure,
  hideWarning,
  setFcrRemove,
  createSuccess,
  setFcrReleased,
  setShiftingList,
  setShiftingRemove,
  getPackingSuccess,
  setDepositaryList,
  setBulkPackingList,
  setShiftingCancel,
  setDepositaryRemove,
  getUnloadingSuccess,
  setPickingOrdersList,
  setPickingOrdersRemove,
  setContainerPackingList,
  cleanPackingByShifting,
  getPackingByShiftingStart,
  getPackingByShiftingFailure,
  getPackingByShiftingSuccess,
  setShiftingReadiness,
  setShiftingReadinessLoading,
  cleanShiftingReadiness,
} = unloadingSlice.actions;

export const { actions, reducer } = unloadingSlice;
export default unloadingSlice.reducer;

const selectPacking = (state: any) => state.viewShipping.unloading.packing;
const selectAggregated = (state: any) =>
  state.viewShipping.unloading.aggregated;
const selectReleased = (state: any) => state.viewShipping.unloading.released;
export const selectShiftingReadiness = (state: any) =>
  state.viewShipping.unloading.shiftingReadiness;

export const getCombinedPackingState = createSelector(
  [selectPacking, selectAggregated],
  (packing, aggregated) => ({
    loading: packing.loading || aggregated.loading,
    success: packing.success || aggregated.success,
    error: packing.error || aggregated.error,
    total: packing.total,
  }),
);

export const getCombinedReleasedState = createSelector(
  [selectReleased, selectAggregated],
  (released, aggregated) => ({
    loading: released.loading || aggregated.loading,
    success: released.success || aggregated.success,
    error: released.error || aggregated.error,
    total: released.total,
  }),
);

export const fetchShiftingDeliveryReadiness =
  (sid: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setShiftingReadinessLoading());
      const data = await getShiftingDeliveryReadinessApi(sid);
      dispatch(setShiftingReadiness(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "shifting", error }));
    }
  };

export const fetchUnloading =
  (id: number, params?: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("unloading"));
      const data = await getUnloadingApi(id, params);
      dispatch(getUnloadingSuccess(data));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "unloading", error }));
    }
  };

export const fetchBulkUnloading =
  (id: number, params?: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("unloading"));
      const data = await getBulkUnloadingApi(id, params);
      dispatch(getUnloadingSuccess(data));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "unloading", error }));
    }
  };

export const updateBulkUnloading =
  ({ id, params }: { id: number; params: Partial<UnloadingBulk> }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("unloading"));
      const data = await updateBulkUnloadingApi(id, params);
      dispatch(setBulkPackingList({ id, data }));
      dispatch(
        createSuccess({
          key: "unloading",
          success: "Unloading details updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "unloading", error }));
    }
  };

export const updateUnloadingNotes =
  ({ id, params }: { id: number; params: UnloadingNotes }): AppThunk =>
  async (dispatch) => {
    try {
      await updateUnloadingNotesApi(id, params);
      dispatch(getSuccess({ key: "notes", items: params }));
      dispatch(
        createSuccess({
          key: "unloading",
          success: "Unloading notes updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "unloading", error }));
    }
  };

export const updateContainerUnloading =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<UnloadingContainer>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("unloading"));
      const data = await updateContainerUnloadingApi(id, params);
      dispatch(setContainerPackingList({ id, data }));
      dispatch(
        createSuccess({
          key: "unloading",
          success: "Unloading details updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "unloading", error }));
    }
  };

export const fetchWarehousing =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("warehousing"));
      const data = await getWarehousingApi(id);
      dispatch(getSuccess({ key: "warehousing", items: data }));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        dispatch(getSuccess({ key: "warehousing", items: null }));
        return;
      }
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "warehousing", error }));
    }
  };

export const updateWarehousing =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<ContainerWarehousingCreate>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("warehousing"));
      const data = await updateWarehousingApi(id, params);
      dispatch(getSuccess({ key: "warehousing", items: data }));
      dispatch(
        createSuccess({
          key: "warehousing",
          success: "Warehousing details updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "warehousing", error }));
    }
  };

export const fetchShifting =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("shifting"));
      const data = await getShiftingApi(id);
      dispatch(getSuccess({ key: "shifting", items: data.items }));
    } catch (err: any) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "shifting", error }));
    }
  };

export const updateShifting =
  ({ id, params }: { id: number; params: Partial<ShiftingCreate> }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateShiftingApi(id, params);
      dispatch(setShiftingList({ id, data }));
      dispatch(
        createSuccess({
          key: "shifting",
          success: "Shifting details updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "shifting", error }));
    }
  };

export const removeShifting =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeShiftingApi(id);
      dispatch(setShiftingRemove(id));
      dispatch(
        createSuccess({
          key: "shifting",
          success: "Shifting removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "shifting", error }));
    }
  };

export const cancelShifting =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      const data = await cancelShiftingApi(id);
      dispatch(setShiftingCancel(data));
      dispatch(
        createSuccess({
          key: "shifting",
          success: "Shifting canceled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "shifting", error }));
    }
  };

export const fetchDepositary =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("depositary"));
      const data = await getDepositaryApi(id);
      dispatch(getSuccess({ key: "depositary", items: data.items }));
    } catch (err: any) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "depositary", error }));
    }
  };

export const updateDepositary =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CreateDepositorChangeForm>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateDepositaryApi(id, params);
      dispatch(setDepositaryList({ id, data }));
      dispatch(
        createSuccess({
          key: "depositary",
          success: "Depositary change updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "depositary", error }));
    }
  };

export const removeDepositary =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeDepositaryApi(id);
      dispatch(setDepositaryRemove(id));
      dispatch(
        createSuccess({
          key: "depositary",
          success: "Depositary change removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "depositary", error }));
    }
  };

export const fetchPickingOrders =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("pickingOrders"));
      const data = await getPickingOrdersApi(id);
      dispatch(getSuccess({ key: "pickingOrders", items: data.items }));
    } catch (err: any) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "pickingOrders", error }));
    }
  };

export const updatePickingOrders =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CreatePickingServiceForm>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updatePickingOrdersApi(id, params);
      dispatch(setPickingOrdersList({ id, data }));
      dispatch(
        createSuccess({
          key: "pickingOrders",
          success: "Service updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "pickingOrders", error }));
    }
  };

export const removePickingOrders =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removePickingOrdersApi(id);
      dispatch(setPickingOrdersRemove(id));
      dispatch(
        createSuccess({
          key: "pickingOrders",
          success: "Service removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "pickingOrders", error }));
    }
  };

export const fetchFcr =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("release"));
      const data = await getFcrApi(id);
      dispatch(getSuccess({ key: "release", items: data.items }));
    } catch (err: any) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "release", error }));
    }
  };

export const updateFcr =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CreateCargoRelease>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateFcrApi(id, params);
      dispatch(setFcrList({ id, data }));
      dispatch(
        createSuccess({
          key: "release",
          success: "FCR updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "release", error }));
    }
  };

export const removeFcr =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeFcrApi(id);
      dispatch(setFcrRemove(id));
      dispatch(
        createSuccess({
          key: "release",
          success: "FCR removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "release", error }));
    }
  };

export const fetchReleased =
  ({ id, params }: { id: number; params: any }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("released"));
      const data = await getFcrPackingListApi(id, params);
      dispatch(
        getSuccess({
          key: "released",
          items: data.items,
          total: data.total,
          page: data.page,
          size: data.size,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "released", error }));
    }
  };

export const updateFcrPackingList =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      packing_list_ids: number[];
      release_date: string;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("released"));
      await updateFcrPackingListApi(id, params);
      dispatch(setFcrReleased({ id, data: params }));
      dispatch(
        createSuccess({
          key: "released",
          success: "FCR Packing List updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "released", error }));
    }
  };

export const fetchPacking =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      page: number;
      size: number;
      [key: string]: string | number | boolean | undefined | null;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("packing"));
      const data = await getPackingShortApi(id, params);
      dispatch(getPackingSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing", error }));
    }
  };

export const fetchPackingListByShifting =
  ({
    sid,
    id,
    params,
  }: {
    sid: number;
    id: number;
    params?: {
      page: number;
      size: number;
      [key: string]: string | number | boolean | undefined | null;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getPackingByShiftingStart(id));
      const data = await getPackingListByShiftingApi(sid, id, params);
      dispatch(getPackingByShiftingSuccess({ id, data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getPackingByShiftingFailure({ id, error }));
    }
  };

export const fetchAggregatedPacking =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      delivery_status__in?: string;
      is_locked_for_fcr?: boolean;
      [key: string]: string | number | boolean | undefined | null;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("aggregated"));
      const data = await getAggregatedPackingShortApi(id, params);
      dispatch(getSuccess({ key: "aggregated", items: data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "aggregated", error }));
    }
  };
